// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'

/**
 * useDebouncedValue — returns a copy of `value` that only updates after `value`
 * has stopped changing for `delayMs`. Keep the raw value bound to the input (so
 * typing stays responsive) and feed the DEBOUNCED value into expensive work
 * (filtering, memoized recomputes, or server queries) so it runs once the user
 * pauses rather than on every keystroke.
 *
 *   const [q, setQ] = useState('')
 *   const debouncedQ = useDebouncedValue(q, 250)
 *   // <input value={q} onChange={e => setQ(e.target.value)} />
 *   const results = useMemo(() => filter(items, debouncedQ), [items, debouncedQ])
 */
export function useDebouncedValue<T>(value: T, delayMs = 250): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(t)
  }, [value, delayMs])
  return debounced
}
